import { Box, List, ListItem, ListItemText, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import {
	DragDropContext,
	DragDropContextProps,
	Draggable,
	DraggableProps,
	DraggableProvided,
	DropResult,
	Droppable,
	DroppableProps,
	DroppableProvided,
} from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { Divider } from "rsuite";
import { ObjectsEnum } from "../..";
import { isAdsEditor } from "../../../../../../../../utils";
import trash from "../../../../../../../assets/svgs/trash.svg";
import ArrowDownOutline from "../../../../../../../components/Icons/ArrowDownOutline";
import DotsVertical from "../../../../../../../components/Icons/DotsVertical";
import Eye from "../../../../../../../components/Icons/Eye";
import EyeCrossed from "../../../../../../../components/Icons/EyeCrossed";
import Locked from "../../../../../../../components/Icons/Locked";
import Unlocked from "../../../../../../../components/Icons/Unlocked";
import { useActiveObject } from "../../../../../../../hooks/useActiveObject";
import UseAdsEditorUtils from "../../../../../../../hooks/useAdsEditorUtils";
import { useEditor } from "../../../../../../../hooks/useEditor";
import { IGroup, ILayer } from "../../../../../../../types";
import styles from "./DraggableList.module.scss";
interface DraggableListProps {
	layerObjects: ILayer[];
	onSelect: (id: string) => void;
	onToggleLock: (id: string, locked: boolean) => void;
	onToggleVisibility: (id: string, visible?: boolean) => void;
	onRemove: (id: string) => void;
	onDragEnd: (newLayerObjects: ILayer[]) => void;
}
const DraggableList = ({
	layerObjects,
	onSelect,
	onToggleLock,
	onToggleVisibility,
	onRemove,
	onDragEnd,
}: DraggableListProps) => {
	const Draggable1 = Draggable as React.ComponentClass<DraggableProps>;
	const DragDropContext1 = DragDropContext as React.ComponentClass<DragDropContextProps>;
	const Droppable1 = Droppable as React.ComponentClass<DroppableProps>;

	const [items, setItems] = useState<ILayer[]>(layerObjects);
	const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
	const editor = useEditor();
	const activeObject = useActiveObject() as Required<any>;
	const { t } = useTranslation("translation", { keyPrefix: "campaignTab.dropDownActions" });
	const {
		isFabricShape,
		isInnerRectangleItem,
		isOuterRectangleItem,
		isSmartImageItem,
		isStaticImage,
		isStaticText,
		isStaticVector,
		isLogoImage,
	} = UseAdsEditorUtils();
	useEffect(() => {
		setItems(layerObjects);
	}, [layerObjects]);
	const handleOnDragEnd = (result: DropResult) => {
		if (!result.destination) return;

		const reorderedItems = Array.from(items);
		const [movedItem] = reorderedItems.splice(result.source.index, 1);
		reorderedItems.splice(result.destination.index, 0, movedItem);
		const draggedItemId = result.draggableId;
		const draggedItem = reorderedItems.find((item) => item.id === draggedItemId);
		if (draggedItem && editor?.canvas?.canvas) {
			const actualIndex = items.length - result.destination.index - 1;
			const canvasObject = editor.canvas.canvas.getObjects().find((obj) => obj.id === draggedItem.id);
			canvasObject?.moveTo(actualIndex + 2);
		}

		setItems(reorderedItems);
		onDragEnd(reorderedItems.reverse());
	};
	const isGroup = (item: ILayer): item is IGroup => {
		return item.type === "group" || item.name === "group";
	};

	const isInnerOrOuterRectangle = (item: ILayer): boolean => {
		return isInnerRectangleItem(item as fabric.Object) || isOuterRectangleItem(item as fabric.Object);
	};

	const toggleGroupExpanded = (groupId: string) => {
		setExpandedGroups((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(groupId)) {
				newSet.delete(groupId);
			} else {
				newSet.add(groupId);
			}
			return newSet;
		});
	};

	const getItemLabel = (item: ILayer) => {
		if (isSmartImageItem(item as fabric.Object)) return t("smartImage");
		if (isOuterRectangleItem(item as fabric.Object)) return t("smartImageBoundary");
		if (isInnerRectangleItem(item as fabric.Object)) return t("mainObject");
		if (isStaticText(item))
			return isAdsEditor() ? (item.textType?.includes("Heading") ? t("heading") : t("body")) : item.textType;
		if (isLogoImage(item)) return isAdsEditor() ? t("logo") : item.logoConfigType;
		if (isStaticImage(item)) return isAdsEditor() ? t("image") : item.imageType;
		if (isFabricShape(item)) return isAdsEditor() ? t("basicShape") : item.shapeType;
		if (isStaticVector(item)) return isAdsEditor() ? t("shape") : item.shapeType;
		if (isGroup(item)) return "Group";

		return t("canvas");
	};

	const renderGroupChildren = (groupItem: any) => {
		if (!isGroup(groupItem)) return null;
		const children = groupItem._objects || groupItem.objects;
		if (!children || children.length === 0) return null;

		const filteredChildren = children.filter((child: any) => !isInnerOrOuterRectangle(child));

		if (filteredChildren.length === 0) return null;

		const isExpanded = expandedGroups.has(groupItem.id);

		if (!isExpanded) return null;

		return (
			<Box className={styles.groupChildren}>
				{filteredChildren.map((child: any) => (
					<Box
						key={child.id}
						className={`${styles.groupChildItem} ${
							activeObject?.id === child.id ? styles.activeLayer : ""
						}`}
					>
						<Box className={styles.layer}>
							<Box className={styles.layerNameContainer}>
								<DotsVertical className={styles.dotsVertical} />
								<ListItemText
									className={styles.layerName}
									primary={getItemLabel(child)}
									onClick={() => onSelect(child.id)}
								/>
							</Box>
							<Box className={styles.actionsContainer}>
								<Box
									className={styles.icon}
									// @ts-ignore
									onClick={() => onToggleLock(child.id, child.lockMovementX)}
								>
									{
										// @ts-ignore
										child.lockMovementX ? <Locked size={24} /> : <Unlocked size={24} />
									}
								</Box>
								<Box
									className={styles.icon}
									onClick={() => onToggleVisibility(child.id, child.visible)}
								>
									{child.visible ? <Eye size={24} /> : <EyeCrossed size={24} />}
								</Box>
								<Box className={styles.icon} onClick={() => onRemove(child.id)}>
									<img src={trash} alt="" />
								</Box>
							</Box>
						</Box>
					</Box>
				))}
			</Box>
		);
	};

	return (
		<DragDropContext1 onDragEnd={handleOnDragEnd}>
			<Droppable1 droppableId="droppable">
				{(provided: DroppableProvided) => (
					<List className={styles.droppableList} {...provided.droppableProps} ref={provided.innerRef}>
						<Box
							className={` ${styles.staticCanvasLayerNameContainer} ${
								activeObject?.id === ObjectsEnum.FRAME ? styles.activeLayer : ""
							}`}
							onClick={() => onSelect(ObjectsEnum.FRAME)}
						>
							<DotsVertical className={styles.dotsVertical} />
							<Typography className={styles.layerName}>{t("canvas")}</Typography>
						</Box>
						<Divider className={styles.divider} />
						{items?.map((item: ILayer, index: number) => (
							<Box key={item.id}>
								<Draggable1 draggableId={item.id} index={index}>
									{(provided: DraggableProvided) => (
										<ListItem
											className={` ${styles.listItem} ${
												activeObject?.id === item.id ? styles.activeLayer : ""
											}`}
											ref={provided.innerRef}
											{...provided.draggableProps}
											{...provided.dragHandleProps}
											sx={{
												marginBottom: 1,
												display: "grid",
												gridTemplateColumns: "1fr 90px",
											}}
										>
											<Box className={` ${styles.layer} `}>
												<Box className={styles.layerNameContainer}>
													{isGroup(item) && (
														<Box
															className={`${styles.expandIcon} ${
																!expandedGroups.has(item.id) ? styles.collapsed : ""
															}`}
															onClick={(e) => {
																e.stopPropagation();
																toggleGroupExpanded(item.id);
															}}
														>
															<ArrowDownOutline size={16} />
														</Box>
													)}
													<DotsVertical className={styles.dotsVertical} />
													<ListItemText
														className={styles.layerName}
														primary={getItemLabel(item)}
														onClick={() => onSelect(item.id)}
													/>
												</Box>
												<Box className={styles.actionsContainer}>
													{
														<Box
															className={styles.icon}
															// @ts-ignore
															onClick={() => onToggleLock(item.id, item.lockMovementX)}
														>
															{
																// @ts-ignore
																item.lockMovementX ? (
																	<Locked size={24} />
																) : (
																	<Unlocked size={24} />
																)
															}
														</Box>
													}
													<Box
														className={styles.icon}
														onClick={() => onToggleVisibility(item.id, item.visible)}
													>
														{item.visible ? <Eye size={24} /> : <EyeCrossed size={24} />}
													</Box>
													<Box className={styles.icon} onClick={() => onRemove(item.id)}>
														<img src={trash} alt="" />
													</Box>
												</Box>
											</Box>
										</ListItem>
									)}
								</Draggable1>
								{isGroup(item) && renderGroupChildren(item)}
								<Divider className={styles.divider} />
							</Box>
						))}
					</List>
				)}
			</Droppable1>
		</DragDropContext1>
	);
};

const ObservedComponent = observer(DraggableList);
export default ObservedComponent;
